import { Container, ContainerProps } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../contexts/app-context';
import {
  useReportRouteLoading,
  useRetryRoute,
} from '../../hooks/use-route-loading';
import { useSessionActions } from '../../hooks/use-session-actions';
import { useAgentAccessDenied } from '../alliance/agent-access';
import { LoadFailedScreen, NoAgentScreen } from '../alliance/system-screens';
import { AnimateInProps } from '../animate-in';
import { AppHeader } from '../app-header';
import { FooterAdCarousel } from '../footer-ad-carousel';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

/**
 * Wraps every top-level page: the document title, the App Bar title (shown
 * on desktop), the optional page header row (`headerContent`; on phones it
 * also carries the page title), then the content, the load error or the
 * error boundary, with the footer ad carousel.
 *
 * While impersonating, if every request for that agent was refused (403),
 * the page shows "You don't have access to this agent's data" with Stop
 * impersonating; the header row stays. `hasError` shows "Couldn't load this page" (offline vs. server trouble)
 * with Try again, which refetches failed queries and remounts the page.
 * While `loading`, the content area is empty and the shell shows the 3px bar
 * under the App Bar after 150ms. The content keeps 88–96px clear at the
 * bottom for the CookieYes and Freshchat buttons.
 */
export function RouteContainer(props: {
  children: React.JSX.Element;
  routeTitle: string;
  headerContent?: React.ReactNode;
  withContainer?: boolean;
  maxWidth?: ContainerProps['maxWidth'];
  loading?: boolean;
  hasError?: boolean;
  paddingBottom?: number;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  noanimation?: boolean;
  // Display full width without spacing for left side navigation
  fullWidth?: boolean;
  hideFooter?: boolean;
  wrapContentForMobile?: boolean;
}) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  // Hooks
  const location = useLocation();
  const retryRoute = useRetryRoute();
  const { stopImpersonating } = useSessionActions();
  const impersonatedAgent = state.impersonatedAgent;
  const agentDenied = useAgentAccessDenied(impersonatedAgent?.agentno);
  useReportRouteLoading({ loading: !!props.loading });

  // Hide footer carousel for admin pages
  const isAdminPath = /^\/admin(\/|$)/.test(location.pathname);

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
  }, [dispatch]);

  React.useEffect(() => {
    // The App Bar shows the page title on desktop
    dispatch({ type: 'SET_ROUTE_TITLE', payload: props.routeTitle });
  }, [dispatch, props.routeTitle]);

  let content = props.children;
  if (props.withContainer) {
    content = <Container maxWidth={props.maxWidth}>{props.children}</Container>;
  }

  let routeContent: JSX.Element | null = null;
  if (impersonatedAgent && agentDenied) {
    // Every request for the impersonated agent came back 403
    routeContent = (
      <NoAgentScreen
        agent={{
          name:
            [impersonatedAgent.fname, impersonatedAgent.lname]
              .filter(Boolean)
              .join(' ') ||
            impersonatedAgent.agentname ||
            impersonatedAgent.agentno,
          id: impersonatedAgent.agentno,
        }}
        onStopImpersonating={stopImpersonating}
      />
    );
  } else if (props.loading) {
    routeContent = null;
  } else if (props.hasError) {
    // Offline vs. server trouble, with Try again (refetch + remount)
    routeContent = <LoadFailedScreen layout="inline" onRetry={retryRoute} />;
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          noanimation={props.noanimation}
          fullWidth={props.fullWidth}
        >
          {content}
          {isAdminPath || props.hideFooter ? null : <FooterAdCarousel />}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Dashboard · {props.routeTitle}</title>
      </Helmet>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          // Clear the CookieYes (bottom-left) and Freshchat (bottom-right)
          // buttons at the end of the page
          pb: { xs: '88px', md: '96px' },
        }}
      >
        <AppHeader
          routeTitle={props.routeTitle}
          headerContent={props.headerContent}
          wrapContentForMobile={props.wrapContentForMobile}
        />

        {routeContent}
      </Box>
    </React.Fragment>
  );
}
